import { infiniteQueryOptions } from '@tanstack/lit-query';
import { api } from 'Api/client';

/** Relative to the client's `/api/` prefix. */
const VIDEO_COMMENTS_BASE_URL = 'videos/';

// Must match `comment/services.py::DEFAULT_COMMENTS_PAGE_SIZE`. Offsets are
// computed on the client (`pageParam + COMMENTS_PAGE_SIZE`); if the two drift,
// pages silently overlap or gap without any error.
export const COMMENTS_PAGE_SIZE = 20;

export interface CommentAuthor {
    first_name: string;
    last_name: string;
    avatar: string | null;
    url: string;
    id?: number;
}

export interface CommentAttachment {
    url: string;
    mimetype: string;
}

export interface CommentData {
    id: number;
    comment: string;
    when: string;
    author: CommentAuthor;
    reply_url: string;
    pm_url: string;
    thumbs_up_count: number;
    user_has_thumbed_up: boolean;
    is_presenter?: boolean;
    is_public?: boolean;
    children?: CommentData[];
    attachments?: CommentAttachment[];
}

export interface CommentsApiResponse {
    count: number;
    total_count: number;
    results: CommentData[];
}

interface FetchCommentsPageParams {
    videoSlug: string;
    limit: number;
    offset: number;
    signal: AbortSignal;
}

export interface PostCommentParams {
    videoSlug: string;
    comment: string;
    attachmentIds?: number[];
    /** Immediate parent comment id — the id of the comment the user clicked
     * "Reply" on, not the top-level ancestor. Backend stores an MPTT tree so
     * the reply chain is preserved (notifications route to `parent.author`)
     * even though the read side flattens all replies of a tree into the root's
     * `children[]`. Serialised on the wire as `parent`. */
    parentId?: number;
}

interface UploadCommentAttachmentParams {
    videoSlug: string;
    file: File;
    signal: AbortSignal;
}

interface CommentThumbupParams {
    videoSlug: string;
    commentId: number;
}

export async function fetchCommentsPage({
    videoSlug,
    limit,
    offset,
    signal,
}: FetchCommentsPageParams): Promise<CommentsApiResponse> {
    return api
        .get<CommentsApiResponse>(`${VIDEO_COMMENTS_BASE_URL}${videoSlug}/comments/`, {
            searchParams: { limit, offset },
            signal,
        })
        .json();
}

export async function uploadCommentAttachment({
    videoSlug,
    file,
    signal,
}: UploadCommentAttachmentParams): Promise<{ id: number }> {
    const formData = new FormData();
    formData.append('upload', file);
    return api
        .post<{ id: number }>(`${VIDEO_COMMENTS_BASE_URL}${videoSlug}/comments/upload/`, {
            body: formData,
            signal,
            // The client's 30s default covers upload time, not just time-to-first-byte,
            // and attachments run to 25MB (MAX_FILE_BYTES) — a large file on a slow
            // uplink would abort mid-transfer. Bump to 5 minutes: still guarantees the
            // upload state machine terminates (a stalled connection surfaces a
            // `TimeoutError` instead of leaving `record.id` undefined forever and
            // pinning the Post button disabled), but survives the 25MB-on-3G worst
            // case. The composer's per-file `AbortController` is still the primary
            // cancellation path.
            timeout: 300_000,
        })
        .json();
}

export async function postComment({
    videoSlug,
    comment,
    attachmentIds,
    parentId,
}: PostCommentParams): Promise<CommentData> {
    const formData = new FormData();
    formData.append('comment', comment);
    attachmentIds?.forEach((id) => formData.append('attachment_ids', String(id)));
    if (parentId !== undefined) formData.append('parent', String(parentId));
    return api.post<CommentData>(`${VIDEO_COMMENTS_BASE_URL}${videoSlug}/comments/`, { body: formData }).json();
}

// Both thumbup calls deliberately skip `.json()` — the response body is unused and
// a 204 would throw a SyntaxError on parse.

export async function thumbsupComment({ videoSlug, commentId }: CommentThumbupParams): Promise<void> {
    await api.post(`${VIDEO_COMMENTS_BASE_URL}${videoSlug}/comments/${commentId}/thumbup/`);
}

export async function removeThumbsupComment({ videoSlug, commentId }: CommentThumbupParams): Promise<void> {
    await api.delete(`${VIDEO_COMMENTS_BASE_URL}${videoSlug}/comments/${commentId}/thumbup/`);
}

/**
 * Shared infinite-query options for a video's comments list. `infiniteQueryOptions()`
 * brands the queryKey and threads the `pageParam` type through so `setQueryData(...)`
 * on the submit path is typed to `InfiniteData<CommentsApiResponse, number>`.
 */
export function commentsQueryOptions(videoSlug: string) {
    return infiniteQueryOptions({
        queryKey: ['videoComments', videoSlug] as const,
        queryFn: ({ pageParam, signal }) =>
            fetchCommentsPage({ videoSlug, limit: COMMENTS_PAGE_SIZE, offset: pageParam, signal }),
        initialPageParam: 0,
        getNextPageParam: (lastPage, _allPages, lastPageParam) => {
            const nextOffset = lastPageParam + COMMENTS_PAGE_SIZE;
            return nextOffset < lastPage.count ? nextOffset : undefined;
        },
    });
}
